import { pollMediaGenerationJob } from '../../api/mediaApi';
import {
  authoringEntityCorrelation,
  PENDO_EVENTS,
  trackAuthoringTranslationOutcome,
} from '../../utils/authoringPendo';
import type { CourseStatus } from '../../constants/contentAuthoringEnums';
import {
  persistCourseTranslationOutcome,
  type CourseTranslationOutcome,
} from './courseTranslationOutcomeState';

const PENDING_BATCHES_KEY = 'course-translation-pending-batches';
const NOTIFIED_JOB_IDS_KEY = 'course-translation-notified-job-ids';
export const TRANSLATION_OUTCOME_UPDATED_EVENT = 'course-translation-outcome-updated';

interface PendingTranslationJob {
  jobId: string;
  languageCode: string;
  languageLabel: string;
  initialStatus?: string;
  outcome?: 'succeeded' | 'failed';
}

interface PendingTranslationBatch {
  batchId: string;
  courseId: string;
  courseName: string;
  courseStatus: CourseStatus;
  jobs: PendingTranslationJob[];
}

export interface RegisterTranslationJobsInput {
  courseId: string;
  courseName: string;
  courseStatus: CourseStatus;
  jobs: Array<{
    jobId: string;
    language: string;
    languageLabel: string;
    status?: string;
  }>;
  onInvalidate?: () => void | Promise<void>;
}

const pollingJobIds = new Set<string>();
let isPollingLoopRunning = false;

function readPendingBatches(): PendingTranslationBatch[] {
  try {
    const raw = sessionStorage.getItem(PENDING_BATCHES_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PendingTranslationBatch[]) : [];
  } catch {
    return [];
  }
}

function writePendingBatches(batches: PendingTranslationBatch[]): void {
  try {
    if (batches.length === 0) {
      sessionStorage.removeItem(PENDING_BATCHES_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_BATCHES_KEY, JSON.stringify(batches));
  } catch {
    /* sessionStorage may be unavailable */
  }
}

function notifyOutcomeUpdated(): void {
  window.dispatchEvent(new CustomEvent(TRANSLATION_OUTCOME_UPDATED_EVENT));
}

function readNotifiedJobIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(NOTIFIED_JOB_IDS_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function addNotifiedJobIds(jobIds: string[]): void {
  if (jobIds.length === 0) {
    return;
  }

  try {
    const notified = readNotifiedJobIds();
    for (const jobId of jobIds) {
      notified.add(jobId);
    }
    sessionStorage.setItem(NOTIFIED_JOB_IDS_KEY, JSON.stringify([...notified]));
  } catch {
    /* sessionStorage may be unavailable */
  }
}

function buildOutcome(batch: PendingTranslationBatch): CourseTranslationOutcome {
  const succeeded = batch.jobs.filter((job) => job.outcome === 'succeeded');
  const failed = batch.jobs.filter((job) => job.outcome === 'failed');

  if (succeeded.length > 0 && failed.length > 0) {
    return {
      courseId: batch.courseId,
      courseName: batch.courseName,
      courseStatus: batch.courseStatus,
      languageLabel: succeeded[0]?.languageLabel ?? '',
      outcome: 'partial',
    };
  }

  if (failed.length > 0) {
    return {
      courseId: batch.courseId,
      courseName: batch.courseName,
      courseStatus: batch.courseStatus,
      languageLabel: failed[0]?.languageLabel ?? '',
      outcome: 'failed',
    };
  }

  return {
    courseId: batch.courseId,
    courseName: batch.courseName,
    courseStatus: batch.courseStatus,
    languageLabel: succeeded[0]?.languageLabel ?? '',
    outcome: 'ready',
  };
}

function finalizeBatchIfComplete(batch: PendingTranslationBatch): PendingTranslationBatch | null {
  const terminalJobs = batch.jobs.filter((job) => job.outcome);
  if (terminalJobs.length < batch.jobs.length) {
    return batch;
  }

  const jobIds = batch.jobs.map((job) => job.jobId);
  const alreadyNotified = jobIds.every((jobId) => readNotifiedJobIds().has(jobId));
  if (!alreadyNotified) {
    persistCourseTranslationOutcome(buildOutcome(batch));
    addNotifiedJobIds(jobIds);
    notifyOutcomeUpdated();
  }
  return null;
}

async function pollJob(
  _batch: PendingTranslationBatch,
  job: PendingTranslationJob,
  onInvalidate?: () => void | Promise<void>,
): Promise<'succeeded' | 'failed' | 'pending'> {
  if (job.outcome) {
    return job.outcome;
  }

  if (job.initialStatus === 'failed') {
    return 'failed';
  }

  if (pollingJobIds.has(job.jobId)) {
    return 'pending';
  }

  pollingJobIds.add(job.jobId);
  try {
    const result = await pollMediaGenerationJob(job.jobId);
    await onInvalidate?.();
    if (result.status === 'succeeded') {
      trackAuthoringTranslationOutcome(PENDO_EVENTS.AUTHORING_TRANSLATION_CREATED, 'success', {
        languageCode: job.languageCode,
        ...authoringEntityCorrelation({ parentCourseId: _batch.courseId }),
      });
      return 'succeeded';
    }
    if (result.status === 'failed') {
      return 'failed';
    }
    return 'pending';
  } catch {
    return 'failed';
  } finally {
    pollingJobIds.delete(job.jobId);
  }
}

async function runPollingLoop(onInvalidate?: () => void | Promise<void>): Promise<void> {
  if (isPollingLoopRunning) {
    return;
  }

  isPollingLoopRunning = true;

  try {
    while (true) {
      const batches = readPendingBatches();
      if (batches.length === 0) {
        break;
      }

      let progressMade = false;
      const nextBatches: PendingTranslationBatch[] = [];

      for (const batch of batches) {
        const updatedJobs = [...batch.jobs];

        for (let index = 0; index < updatedJobs.length; index += 1) {
          const job = updatedJobs[index];
          if (job.outcome) {
            continue;
          }

          const result = await pollJob(batch, job, onInvalidate);
          if (result === 'pending') {
            continue;
          }

          updatedJobs[index] = { ...job, outcome: result };
          progressMade = true;
        }

        const updatedBatch = { ...batch, jobs: updatedJobs };
        const finalized = finalizeBatchIfComplete(updatedBatch);
        if (finalized) {
          nextBatches.push(finalized);
        }
      }

      writePendingBatches(nextBatches);

      if (!progressMade) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 3000);
        });
      }
    }
  } finally {
    isPollingLoopRunning = false;
  }
}

let invalidateCallback: (() => void | Promise<void>) | undefined;

export function registerTranslationJobs(input: RegisterTranslationJobsInput): void {
  if (input.onInvalidate) {
    invalidateCallback = input.onInvalidate;
  }

  const batches = readPendingBatches();
  const existingJobIds = new Set(batches.flatMap((batch) => batch.jobs.map((job) => job.jobId)));
  const notifiedJobIds = readNotifiedJobIds();
  const newJobs = input.jobs.filter(
    (job) => !existingJobIds.has(job.jobId) && !notifiedJobIds.has(job.jobId),
  );

  if (newJobs.length > 0) {
    const batch: PendingTranslationBatch = {
      batchId: `${input.courseId}-${Date.now()}`,
      courseId: input.courseId,
      courseName: input.courseName,
      courseStatus: input.courseStatus,
      jobs: newJobs.map((job) => ({
        jobId: job.jobId,
        languageCode: job.language,
        languageLabel: job.languageLabel,
        initialStatus: job.status,
      })),
    };
    batches.push(batch);
    writePendingBatches(batches);
  }

  void runPollingLoop(invalidateCallback);
}

export function resumeTranslationJobPolling(onInvalidate?: () => void | Promise<void>): void {
  if (onInvalidate) {
    invalidateCallback = onInvalidate;
  }

  const batches = readPendingBatches();
  if (batches.length === 0) {
    return;
  }

  void runPollingLoop(invalidateCallback);
}
